import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  MoreVertical,
  Eye,
  Download,
  Send,
  Truck,
  FileText,
  Copy,
  PersonStanding,
  FileSearch,
} from 'lucide-react';
import { useGenerateBOL, usePreviewBOL } from '../../features/carriers/api';
import { toast } from 'react-toastify';
import { useAssignCustomerToSalesRep } from '../../features/customers/api';
import { useCurrentUser } from '../../features/auth/api';
import { BOLPreviewDialog } from '../BOLPreviewDialog/BOLPreviewDialog';
import { blobErrorMessage } from '../../utils/errors';
import './OrderActionsDropdown.scss';
import { ReplicateOrderDialog } from '../ReplicateOrderDialog/ReplicateOrderDialog';

interface OrderActionsDropdownProps {
  item: any;
  onManage: () => void;
  onViewNotes: () => void;
  onSendBOL?: (item: any) => void;
  /** Open a specific load's details modal (used when one load is replicated). */
  onOpenItem?: (itemId: string) => void;
}

export function OrderActionsDropdown({
  item,
  onManage,
  onViewNotes,
  onSendBOL,
  onOpenItem,
}: OrderActionsDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(null);
  const generateBOL = useGenerateBOL();
  const previewBOL = usePreviewBOL();
  const assignCustomerToSalesRep = useAssignCustomerToSalesRep(item.order.customerId);
  const { data: me } = useCurrentUser();
  const [isReplicateDialogOpen, setIsReplicateDialogOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const canUseBOL =
    ['APPROVED', 'ASSIGNED', 'READY_FOR_PICKUP'].includes(item.status) && item.carrier;

  // After the portal renders, clamp top so the menu never overflows the viewport
  useEffect(() => {
    if (!isOpen || !menuRef.current || !menuPos) return;
    const { height } = menuRef.current.getBoundingClientRect();
    const overflow = menuPos.top + height - window.innerHeight;
    if (overflow > 0) {
      setMenuPos((p) => p && { ...p, top: p.top - overflow - 4 });
    }
  }, [isOpen, menuPos]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target) &&
        menuRef.current &&
        !menuRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', () => setIsOpen(false), { capture: true, once: true });
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  /** Revoke the object URL on explicit close so the blob link stays valid while open. */
  const closePreview = useCallback(() => {
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  // Split out of the click handler so the preview dialog can reuse it
  const downloadBOL = async () => {
    try {
      await generateBOL.mutateAsync({ itemId: item.id, loadNumber: item.loadNumber });
      toast.success('BOL downloaded');
    } catch (error) {
      toast.error(await blobErrorMessage(error, 'Failed to download BOL'));
    }
  };

  const handleDownloadBOL = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(false);
    downloadBOL();
  };

  const handlePreviewBOL = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(false);
    try {
      const url = await previewBOL.mutateAsync(item.id);
      setPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
    } catch (error) {
      toast.error(await blobErrorMessage(error, 'Failed to generate BOL preview'));
    }
  };

  const handleSendBOL = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(false);
    if (onSendBOL) {
      onSendBOL(item);
    }
  };

  const handleManage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(false);
    onManage();
  };

  const handleViewNotes = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(false);
    onViewNotes();
  };

  const handleReplicate = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(false);
    setIsReplicateDialogOpen(true);
  };

  // Reps live on the delivery location. Admins can only claim a location with
  // no rep yet; a load without a delivery location has nothing to claim.
  const canClaimLocation = Boolean(
    item.order?.deliveryAddress?.id && !item.order.deliveryAddress.salesRep
  );

  const handleAssignToMe = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(false);
    try {
      // Assign the load's delivery location to me.
      await assignCustomerToSalesRep.mutateAsync(item.order?.deliveryAddress?.id);
      toast.success('Assigned to you');
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Failed to assign customer');
    }
  };

  return (
    <div className="order-actions-dropdown" ref={dropdownRef}>
      <button
        ref={triggerRef}
        className="order-actions-dropdown__trigger"
        onClick={(e) => {
          e.stopPropagation();
          if (!isOpen && triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
          }
          setIsOpen(!isOpen);
        }}
        aria-label="Order actions"
      >
        <MoreVertical size={16} />
      </button>

      {isOpen &&
        menuPos &&
        createPortal(
          <div
            ref={menuRef}
            className="order-actions-dropdown__menu"
            style={{ position: 'fixed', top: menuPos.top, right: menuPos.right, zIndex: 1000 }}
          >
            <button className="order-actions-dropdown__item" onClick={handleManage}>
              <Eye size={14} />
              <span>Manage Order</span>
            </button>
            <button className="order-actions-dropdown__item" onClick={handleViewNotes}>
              <FileText size={14} />
              <span>View Notes</span>
            </button>
            <button className="order-actions-dropdown__item" onClick={handleReplicate}>
              <Copy size={14} />
              <span>Replicate Order</span>
            </button>
            {me?.type === 'ORG_ADMIN' && canClaimLocation && (
              <button className="order-actions-dropdown__item" onClick={handleAssignToMe}>
                <PersonStanding size={14} />
                <span>Assign Location to Me</span>
              </button>
            )}
            {canUseBOL && (
              <>
                <div className="order-actions-dropdown__divider" />
                <button
                  className="order-actions-dropdown__item"
                  onClick={handlePreviewBOL}
                  disabled={previewBOL.isPending}
                >
                  <FileSearch size={14} />
                  <span>{previewBOL.isPending ? 'Loading...' : 'Preview BOL'}</span>
                </button>
                <button
                  className="order-actions-dropdown__item"
                  onClick={handleDownloadBOL}
                  disabled={generateBOL.isPending}
                >
                  <Download size={14} />
                  <span>{generateBOL.isPending ? 'Generating...' : 'Download BOL'}</span>
                </button>
                <button className="order-actions-dropdown__item" onClick={handleSendBOL}>
                  <Send size={14} />
                  <span>Send BOL</span>
                </button>
              </>
            )}
            {!item.carrier && (item.status === 'APPROVED' || item.status === 'SHIPPED') && (
              <>
                <div className="order-actions-dropdown__divider" />
                <div className="order-actions-dropdown__item order-actions-dropdown__item--disabled">
                  <Truck size={14} />
                  <span className="muted tiny">Assign carrier to use BOL</span>
                </div>
              </>
            )}
          </div>,
          document.body
        )}

      {previewUrl && (
        <BOLPreviewDialog
          url={previewUrl}
          loadNumber={item.loadNumber}
          onClose={closePreview}
          onDownload={() => {
            downloadBOL();
            closePreview();
          }}
          onSend={() => {
            closePreview();
            onSendBOL?.(item);
          }}
        />
      )}

      <ReplicateOrderDialog
        isOpen={isReplicateDialogOpen}
        onClose={() => setIsReplicateDialogOpen(false)}
        orderId={item.order.id}
        sourceItemId={item.id}
        sourceLoadNumber={item.loadNumber || `#${item.order?.id?.slice(-6) || ''}`}
        customerId={item.cust?.id ?? item.order?.customerId ?? ''}
        customerName={item.cust?.name ?? 'Customer'}
        hasCarrier={Boolean(item.carrierId)}
        defaults={{
          productId: item.productId ?? item.product?.id ?? '',
          deliveryAddressId: item.order?.deliveryAddress?.id ?? '',
          orgLoadOptionId: item.orgLoadOptionIdSnapshot ?? '',
          quantity: Number(item.quantity) || 0,
        }}
        onReplicatedItem={onOpenItem}
      />
    </div>
  );
}
